import { z } from 'zod';
import { ConfidenceBand, FunctionalPermission } from './vocabularies';
import { IsoDate, Timestamp, Uuid } from './envelope';
import { MetricStatus } from './metric';

/**
 * The query envelope wrapped around every analytical response.
 *
 * Source: Prompt 3 "Required query metadata" — source status, refreshedAt,
 * quality summary, confidence, metric definition version, selected period,
 * authorization scope and allowed actions.
 *
 * Putting these on the envelope rather than leaving them to each endpoint is
 * what makes the ten page states implementable as a shared component: the shell
 * can decide between "stale", "partial" and "source failure" without any screen
 * writing that logic itself.
 */

// ── Freshness and trust ──────────────────────────────────────────────────────

export const SourceStatus = z.object({
  /** Worst status across every source contributing to this response. */
  overall: MetricStatus,
  refreshedAt: Timestamp.nullable(),
  /**
   * Populated when the most recent refresh failed. The prior successful data is
   * still returned; the UI must show both, never silently serve stale data as
   * current (UX-S06).
   */
  lastGoodAt: Timestamp.nullable(),
  failedRunAt: Timestamp.nullable(),
  contributingSources: z.array(
    z.object({
      sourceSystem: z.string(),
      status: MetricStatus,
      refreshedAt: Timestamp.nullable(),
      recordCount: z.number().int().nonnegative().nullable(),
    }),
  ),
});
export type SourceStatus = z.infer<typeof SourceStatus>;

export const QualitySummary = z.object({
  /** 0–100 composite across the six DQ dimensions. */
  score: z.number().min(0).max(100).nullable(),
  completeness: z.number().min(0).max(1).nullable(),
  validity: z.number().min(0).max(1).nullable(),
  uniqueness: z.number().min(0).max(1).nullable(),
  consistency: z.number().min(0).max(1).nullable(),
  timeliness: z.number().min(0).max(1).nullable(),
  openIssueCount: z.number().int().nonnegative(),
});
export type QualitySummary = z.infer<typeof QualitySummary>;

/**
 * The scope this response was computed under. Echoed back so the UI can tell the
 * user *why* they are seeing these numbers — the difference between "the
 * portfolio is healthy" and "the part of the portfolio you can see is healthy"
 * matters a great deal to a department leader.
 */
export const AuthorizationScope = z.object({
  scopeId: Uuid,
  scopeType: z.enum(['Enterprise', 'Department', 'ValueStream', 'ProductGroup', 'Product']),
  scopeLabel: z.string(),
  /** True when the caller's span is narrower than the requested scope. */
  isNarrowed: z.boolean(),
  /** Fields removed from this response by field-level policy. */
  maskedFields: z.array(z.string()),
});
export type AuthorizationScope = z.infer<typeof AuthorizationScope>;

/**
 * Actions the caller may take, computed server-side from role + scope +
 * ownership + sensitivity + workflow state. The UI enables controls from this
 * list; it never decides for itself. Hiding a button is presentation, not
 * security, so the same policy is re-evaluated on the command.
 */
export const AllowedAction = z.object({
  action: z.string().min(1),
  permission: FunctionalPermission,
  enabled: z.boolean(),
  /** Why an action is present but disabled — shown as a tooltip. */
  disabledReason: z.string().nullable(),
});
export type AllowedAction = z.infer<typeof AllowedAction>;

export const QueryMetadata = z.object({
  correlationId: z.string().min(1),
  periodStart: IsoDate,
  periodEnd: IsoDate,
  comparisonPeriodStart: IsoDate.nullable(),
  comparisonPeriodEnd: IsoDate.nullable(),
  /** Set when viewing history. Renders the read-only as-of banner (UX-S10). */
  asOf: IsoDate.nullable(),
  sourceStatus: SourceStatus,
  quality: QualitySummary,
  confidence: ConfidenceBand.nullable(),
  scope: AuthorizationScope,
  allowedActions: z.array(AllowedAction),
  /** Seed data must be visibly labelled as illustrative wherever it appears. */
  isSyntheticData: z.boolean(),
});
export type QueryMetadata = z.infer<typeof QueryMetadata>;

/** Wrap any payload in the standard envelope. */
export function queryResult<T extends z.ZodTypeAny>(payload: T) {
  return z.object({
    data: payload,
    meta: QueryMetadata,
  });
}

export interface QueryResult<T> {
  data: T;
  meta: QueryMetadata;
}

// ── Filtering, sorting, paging ───────────────────────────────────────────────

/**
 * Global filters. These are URL-persisted, inherited by drill-down and by
 * export, which is why they are a single named shape rather than loose params.
 */
export const GlobalFilters = z.object({
  scopeId: Uuid.optional(),
  periodStart: IsoDate.optional(),
  periodEnd: IsoDate.optional(),
  comparison: z.enum(['none', 'priorPeriod', 'priorYear', 'scenario']).optional(),
  asOf: IsoDate.optional(),
  departmentIds: z.array(Uuid).optional(),
  valueStreamIds: z.array(Uuid).optional(),
  productGroupIds: z.array(Uuid).optional(),
  productIds: z.array(Uuid).optional(),
  ownerIds: z.array(Uuid).optional(),
  health: z.array(z.string()).optional(),
  lifecycle: z.array(z.string()).optional(),
  classification: z.array(z.string()).optional(),
  scenarioId: Uuid.optional(),
  search: z.string().optional(),
});
export type GlobalFilters = z.infer<typeof GlobalFilters>;

export const SortDirection = z.enum(['asc', 'desc']);
export type SortDirection = z.infer<typeof SortDirection>;

export const PageRequest = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(200).default(25),
  sortBy: z.string().optional(),
  sortDirection: SortDirection.optional(),
});
export type PageRequest = z.infer<typeof PageRequest>;

export function paged<T extends z.ZodTypeAny>(row: T) {
  return z.object({
    rows: z.array(row),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    /**
     * Null when counting would cost more than the query itself — the
     * architecture permits omitting a total rather than paying for it. The grid
     * falls back to "load more" when this is null.
     */
    totalRows: z.number().int().nonnegative().nullable(),
    hasMore: z.boolean(),
  });
}

export interface Paged<T> {
  rows: T[];
  page: number;
  pageSize: number;
  totalRows: number | null;
  hasMore: boolean;
}

// ── Page states ──────────────────────────────────────────────────────────────

/**
 * The ten states every route implements (UX-S01…UX-S10). Modelled as a closed
 * union so a screen cannot forget one: the StatePanel component switches
 * exhaustively over this type and TypeScript fails the build on a missing arm.
 */
export const PageState = z.enum([
  'loading', // UX-S01
  'empty', // UX-S02 — no data in scope
  'partial', // UX-S03 — some sources unavailable
  'stale', // UX-S04 — last refresh is older than threshold
  'validationError', // UX-S05
  'sourceFailure', // UX-S06 — current run failed, last-good shown
  /*
    The service could not be reached at all.

    Distinct from `sourceFailure`, which says a *data run* failed and that
    last-good figures are on screen. When the API is unreachable neither claim
    holds: nothing ran, and nothing is being shown. Collapsing the two meant a
    reader whose connection had dropped was told their pipeline had broken and,
    underneath, that their entitlements were too narrow — three wrong
    statements from one missing server.
  */
  'serviceUnreachable',
  'unauthorized', // UX-S07 — reveals nothing about existence
  'conflict', // UX-S08 — competing values await a steward
  'pendingApproval', // UX-S09 — draft separate from published
  'historical', // UX-S10 — read-only as-of view
  'ready',
]);
export type PageState = z.infer<typeof PageState>;

/**
 * Derive the page state from the response envelope.
 *
 * Centralised so that "what counts as stale" is one decision rather than
 * twenty-two slightly different ones scattered across the routes.
 */
export function derivePageState(
  meta: Pick<QueryMetadata, 'sourceStatus' | 'asOf'>,
  options: { hasRows: boolean; isAuthorized?: boolean } = { hasRows: true },
): PageState {
  if (options.isAuthorized === false) return 'unauthorized';
  if (meta.asOf) return 'historical';
  if (meta.sourceStatus.failedRunAt) return 'sourceFailure';
  if (meta.sourceStatus.overall === 'Stale') return 'stale';
  if (meta.sourceStatus.overall === 'Partial') return 'partial';
  if (meta.sourceStatus.overall === 'Error') return 'sourceFailure';
  if (!options.hasRows) return 'empty';
  return 'ready';
}
